import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import {
  fetchProductReviews,
  fetchProductReviewById,
  deleteProductReviewApi,
} from '../../utils/productReviewApi';
import {
  getProductReviewId,
  getProductId,
  getUserId,
  formatReviewDate,
  truncateText,
} from './productReviewUtils';
import ProductReviewCreate from './ProductReviewCreate';
import ProductReviewEdit from './ProductReviewEdit';
import '../css/Category.css';

const ProductReviewPage = () => {
  const [searchParams] = useSearchParams();
  const filterProductId = searchParams.get('productId') || '';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState(null);

  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(1);
  const [PAGE_SIZE] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const showToast = (message, type = 'danger') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchList = async (currentPage, pageSize, search, productId) => {
    setLoading(true);
    setError(null);
    try {
      const { items: rows, totalRecords } = await fetchProductReviews({
        pageIndex: currentPage,
        pageSize,
        search,
        productId: productId || undefined,
      });
      setItems(rows);
      setTotalCount(totalRecords);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(page, PAGE_SIZE, filterText, filterProductId);
  }, [page, PAGE_SIZE, filterText, filterProductId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = async (row) => {
    const id = getProductReviewId(row);
    try {
      const data = await fetchProductReviewById(id);
      setEditItem(data ?? row);
    } catch {
      setEditItem(row);
    }
    setShowEditModal(true);
  };

  const handleDelete = async (row) => {
    const id = getProductReviewId(row);
    if (!window.confirm(`Delete review #${id}?`)) return;
    try {
      await deleteProductReviewApi(id);
      showToast('Review deleted successfully.', 'success');
      fetchList(page, PAGE_SIZE, filterText, filterProductId);
    } catch (e) {
      showToast(e.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Product Reviews</span>
        {filterProductId ? <span> (Product #{filterProductId})</span> : null}
      </p>

      <div className="card">
        <div className="card-body">
          {toast && <div className={`alert alert-${toast.type} py-2 mb-3`}>{toast.message}</div>}

          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h4 className="card-title mb-0">Product Reviews</h4>
            <div className="d-flex align-items-center gap-2">
              <div className="cat-filter-wrap">
                <i className="mdi mdi-magnify cat-filter-icon"></i>
                <input
                  type="text"
                  className="cat-filter-input"
                  placeholder="Search..."
                  value={filterText}
                  onChange={(e) => { setFilterText(e.target.value); setPage(1); }}
                />
              </div>
              <button type="button" className="cat-add-btn" onClick={() => setShowAddModal(true)}>
                <i className="mdi mdi-plus" style={{ marginRight: 6 }}></i> Add Review
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Product</th>
                    <th>User</th>
                    <th>Rating</th>
                    <th>Title</th>
                    <th>Review</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-4 text-muted">No reviews found.</td></tr>
                  ) : items.map((row) => {
                    const id = getProductReviewId(row);
                    return (
                      <tr key={id}>
                        <td>{id}</td>
                        <td>{getProductId(row)}</td>
                        <td>{getUserId(row)}</td>
                        <td>{row.Rating ?? row.rating}</td>
                        <td>{row.ReviewTitle ?? row.reviewTitle ?? '—'}</td>
                        <td>{truncateText(row.ReviewText ?? row.reviewText)}</td>
                        <td>{formatReviewDate(row.CreatedAt ?? row.createdAt)}</td>
                        <td>
                          <button type="button" className="btn btn-sm btn-outline-primary me-1" style={{ border: 'none' }} onClick={() => openEdit(row)}>
                            <i className="mdi mdi-pencil"></i>
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-danger" style={{ border: 'none' }} onClick={() => handleDelete(row)}>
                            <i className="mdi mdi-delete"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && totalCount > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="cat-page-info">Showing page {safePage} of {totalPages} ({totalCount} total)</span>
              <div className="cat-pagination">
                <button type="button" className="cat-page-btn" disabled={safePage <= 1} onClick={() => setPage((p) => p - 1)}>
                  <i className="mdi mdi-chevron-left"></i>
                </button>
                <button type="button" className="cat-page-btn" disabled={safePage >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  <i className="mdi mdi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <ProductReviewCreate
          defaultProductId={filterProductId}
          onClose={() => setShowAddModal(false)}
          onSuccess={(msg) => {
            setShowAddModal(false);
            showToast(msg, 'success');
            fetchList(page, PAGE_SIZE, filterText, filterProductId);
          }}
        />
      )}

      {showEditModal && editItem && (
        <ProductReviewEdit
          editItem={editItem}
          onClose={() => { setShowEditModal(false); setEditItem(null); }}
          onSuccess={(msg) => {
            setShowEditModal(false);
            setEditItem(null);
            showToast(msg, 'success');
            fetchList(page, PAGE_SIZE, filterText, filterProductId);
          }}
        />
      )}
    </AdminLayout>
  );
};

export default ProductReviewPage;
